import { useCallback, useContext, useMemo } from 'react'

import { type SanityFrameBuilderSpacerColor } from '@frame-builder/data/sanity/queries/types/spacer'
import { FrameBuilderSettingsContext } from '@frame-builder/contexts/settings-context'
import { FrameBuilderSelectedStateContext } from '@frame-builder/contexts/state-context'
import { FrameBuilderStringsContext } from '@frame-builder/contexts/strings-context'
import { FrameBuilderValidationContext } from '@frame-builder/contexts/validation-context'

import ListContainer from '@frame-builder/components/utility/list-container'
import SmallGraphic from '@frame-builder/components/utility/small-graphic'

interface SpacerSizeDetailsProps {
  spacerSizeId: string
}

const SpacerSizeDetails = ({ spacerSizeId }: SpacerSizeDetailsProps) => {
  const { selectedState, dispatchSelectedState } = useContext(
    FrameBuilderSelectedStateContext,
  )
  const { frameBuilderConfiguration } = useContext(FrameBuilderSettingsContext)
  const frameBuilderStrings = useContext(FrameBuilderStringsContext)
  const { lastValidState } = useContext(FrameBuilderValidationContext)

  const spacerSize = useMemo(() => {
    return frameBuilderConfiguration.spacerSizes.find(
      (spacerSize) => spacerSize._id === spacerSizeId,
    )
  }, [frameBuilderConfiguration, spacerSizeId])

  const spacerColors = useMemo(() => {
    return frameBuilderConfiguration.spacerColors
  }, [frameBuilderConfiguration])

  const selectColor = useCallback(
    (spacerColor: SanityFrameBuilderSpacerColor) => {
      dispatchSelectedState({
        type: 'spacer',
        color: spacerColor,
      })
    },
    [dispatchSelectedState],
  )

  if (!spacerSize || (spacerSize.thickness === 0 && !spacerSize.description)) {
    return
  }

  return (
    <div className="w-full pt-2.5 pb-4 px-2.5 space-y-4">
      {!!spacerSize.description && (
        <div className="flex text-xs border-t pt-2.5">
          {spacerSize.description}
        </div>
      )}

      {spacerSize.thickness > 0 && (
        <div className="flex flex-col gap-2.5">
          <p className="flex gap-2.5">
            <span>{frameBuilderStrings.spacerStepColorLabel}</span>
            {!!selectedState.spacerColor && (
              <span className="text-gray-700">
                {selectedState.spacerColor.title}
              </span>
            )}
          </p>

          <ListContainer gapSize="xs">
            {spacerColors.map((spacerColor) => (
              <SmallGraphic
                key={spacerColor._id}
                backgroundColor={spacerColor.color?.hex}
                selected={spacerColor._id === selectedState.spacerColor?._id}
                error={lastValidState.spacerColor?._id === spacerColor._id}
                onClick={() => selectColor(spacerColor)}
              />
            ))}
          </ListContainer>
        </div>
      )}
    </div>
  )
}

export default SpacerSizeDetails
